import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TableBox } from "@shared/components/Table";
import { SubscriptionPlanDetailsDto } from "@shared/apis/sidekick/generated";
import styled from "@emotion/styled";
import { formatToCurrency } from "@shared/utils/formatters";

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1rem;
`;

const AccordionItem = styled(Accordion)`
  border-radius: 1rem !important;
  overflow: hidden;
  &:hover {
    background-color: ${(p) => p.theme.colors.background2};
  }
`;

const CustomAccordionSummary = styled(AccordionSummary)`
  border-radius: 1rem;
  overflow: hidden;
`;

const ContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TextGroup = styled(Typography)`
  display: grid;
  gap: 2rem;
  align-items: center;
  grid-template-columns: 200px auto;
  font-size: ${(p) => p.theme.text.fontSizes.m};
  margin-left: 1rem;
  label {
    font-weight: 600;
  }
`;

type Props = {
  subscriptionPlans: SubscriptionPlanDetailsDto[];
  isLoadingSubscriptionPlans: boolean;
};

export const SubscriptionPlansList = (props: Props) => {
  const { subscriptionPlans, isLoadingSubscriptionPlans } = props;

  const formatAllowedLoginMethods = (arr: string[]) => {
    return arr.map((method) => {
      if (method === "email") {
        return "Sidekick credentials";
      }
      if (method === "mls-sso") {
        return "MLS SSO";
      }
      return method;
    });
  };

  return (
    <TableBox>
      <ListContainer>
        {isLoadingSubscriptionPlans && subscriptionPlans.length === 0 && (
          <ContentContainer sx={{ alignItems: "center", padding: "2rem" }}>
            <CircularProgress />
          </ContentContainer>
        )}

        {!isLoadingSubscriptionPlans && subscriptionPlans.length === 0 && (
          <ContentContainer>
            <Typography variant="body2">No subscription plans found</Typography>
          </ContentContainer>
        )}

        {!isLoadingSubscriptionPlans &&
          subscriptionPlans.map((plan) => {
            return (
              <AccordionItem
                tabIndex={-1}
                key={plan.id}
                slotProps={{ transition: { unmountOnExit: true } }}
                elevation={0}
              >
                <CustomAccordionSummary
                  expandIcon={<ExpandMoreIcon color="secondary" />}
                  aria-controls="subscription-plan-content"
                  id={`plan-${plan.id}`}
                >
                  <Typography variant="h6" fontWeight={600}>
                    {plan.name}
                  </Typography>
                </CustomAccordionSummary>
                <AccordionDetails>
                  <ContentContainer>
                    <Typography variant="h6">General</Typography>
                    <TextGroup>
                      <label>Private description</label>{" "}
                      <span>{plan.privateDescription}</span>
                    </TextGroup>
                    <TextGroup>
                      <label>Public description</label>{" "}
                      <span>{plan.publicDescription}</span>
                    </TextGroup>
                    <TextGroup>
                      <label>Allowed login methods</label>{" "}
                      <span>
                        {formatAllowedLoginMethods(
                          plan.allowedLoginMethods
                        ).join(", ")}
                      </span>
                    </TextGroup>
                    <TextGroup>
                      <label>Accepting new subscribers</label>{" "}
                      <span>{plan.acceptingNewSubscribers ? "Yes" : "No"}</span>
                    </TextGroup>

                    <Typography variant="h6" mt={2}>
                      Access
                    </Typography>
                    <TextGroup>
                      <label>Allowed MLS providers</label>{" "}
                      <span>{plan.access.allowedMlsProviders.join(", ")}</span>
                    </TextGroup>
                    <TextGroup>
                      <label>Allowed states</label>{" "}
                      <span>{plan.access.allowedStates.join(", ")}</span>
                    </TextGroup>

                    <Typography variant="h6" mt={2}>
                      Pricing
                    </Typography>
                    {plan.price && (
                      <>
                        <TextGroup>
                          <label>Monthly price</label>{" "}
                          <span>
                            {formatToCurrency(plan.price?.monthlyPrice / 100)}
                          </span>
                        </TextGroup>
                        <TextGroup>
                          <label>Trial days</label>{" "}
                          <span>{plan.price.trialDays}</span>
                        </TextGroup>
                      </>
                    )}
                    {!plan.price && (
                      <TextGroup>
                        <label>Monthly price</label> <span>Free</span>
                      </TextGroup>
                    )}
                  </ContentContainer>
                </AccordionDetails>
              </AccordionItem>
            );
          })}
      </ListContainer>
    </TableBox>
  );
};
